import { useQueries, useQuery } from '@tanstack/react-query';
import { getTimeTillEndOfDayMs } from '@toggle/helpers';
import { DocumentEntityItem, DocumentSection } from '@toggle/toggle';

import {
  getDocument,
  getDocumentSection,
  getDocumentsEntity,
  getDocumentsEntityCount,
} from './documents-entity-service';

const mapDocuments = (document: DocumentEntityItem) => ({
  ...document,
  key: document.name as string,
});

export const QUERY_KEY_DOCUMENTS_ENTITY = 'QUERY_KEY_DOCUMENTS_ENTITY';
export const useDocumentsEntity = (entityTag: string) =>
  useQuery({
    queryKey: [QUERY_KEY_DOCUMENTS_ENTITY, entityTag],
    queryFn: ({ signal }) => getDocumentsEntity(entityTag, signal),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!entityTag,
    select: data => data?.data?.map(mapDocuments) ?? [],
  });

export const QUERY_KEY_DOCUMENTS_ENTITY_COUNT =
  'QUERY_KEY_DOCUMENTS_ENTITY_COUNT';
export const useDocumentsEntityCount = (entityTag: string) =>
  useQuery({
    queryKey: [QUERY_KEY_DOCUMENTS_ENTITY_COUNT, entityTag],
    queryFn: ({ signal }) => getDocumentsEntityCount(entityTag, signal),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!entityTag,
    select: data => data?.data?.public ?? 0,
  });

export const QUERY_KEY_DOCUMENT_SECTION = 'QUERY_KEY_DOCUMENT_SECTION';

export const useDocumentSections = (evidence: string[]) => {
  const queries = evidence.map(id => ({
    queryKey: [QUERY_KEY_DOCUMENT_SECTION, id],
    queryFn: () => getDocumentSection(id),
    enabled: !!evidence.length,
    staleTime: getTimeTillEndOfDayMs(),
  }));

  return useQueries({
    queries,
    combine: results => ({
      data: results.map(r => r.data).filter(Boolean) as DocumentSection[],
      isFetching: results.some(r => r.isFetching),
      isError: !!results.length && results.every(r => r.isError),
    }),
  });
};

export const QUERY_KEY_DOCUMENT = 'QUERY_KEY_DOCUMENT';

export const useDocuments = (docIds: string[]) => {
  const queries = docIds.map(id => ({
    queryKey: [QUERY_KEY_DOCUMENT, id],
    queryFn: () => getDocument(id),
    staleTime: getTimeTillEndOfDayMs(),
    enabled: !!id,
  }));

  return useQueries({
    queries,
    combine: results => ({
      data: results.map(r => r.data),
      isFetching: results.some(r => r.isFetching),
      isError: results.some(r => r.isError),
    }),
  });
};
